import { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Money on a streaming statement is often fractions of a cent, so the number
 * of decimals follows the size of the amount: $12.40, $0.0349, $0.000771.
 */
export function money(x, { places } = {}) {
  const v = Number(x || 0);
  const a = Math.abs(v);
  const d = places ?? (a >= 1 ? 2 : a >= 0.01 ? 4 : 6);
  const s = `$${a.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
  return v < -1e-12 ? `−${s}` : s;
}

export function Money({ value, className, places, bold }) {
  const neg = Number(value || 0) < -1e-12;
  return <span className={cn("font-mono tabular-nums whitespace-nowrap", neg && "text-stock-out", bold && "font-semibold", className)}>{money(value, { places })}</span>;
}

/** "2021-04" -> "Apr 2021" */
export function monthLabel(p) {
  const q = /^(\d{4})-Q(\d)$/.exec(p || "");
  if (q) return `Q${q[2]} ${q[1]}`;
  if (!p || p.length < 7) return p || "—";
  return `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}`;
}

export function monthRange(p) {
  const q = /^(\d{4})-Q(\d)$/.exec(p || "");
  if (q) {
    const m = (Number(q[2]) - 1) * 3 + 1;
    const end = new Date(Number(q[1]), m + 2, 0).getDate();
    return { from: `${q[1]}-${String(m).padStart(2, "0")}-01`, to: `${q[1]}-${String(m + 2).padStart(2, "0")}-${end}` };
  }
  const [y, m] = p.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${p}-01`, to: `${p}-${String(last).padStart(2, "0")}` };
}

/** Shortens the MLC's service names for tight places. */
export function serviceName(s) {
  const map = { "SoundCloud Operations Inc.": "SoundCloud", "Securus Technologies, LLC": "Securus" };
  return map[s] || (s || "").trim();
}

/** Plain horizontal bars — the label, a bar, the amount. Negative amounts go red. */
export function Bars({ items, onPick, labelWidth = 110, activeKey, places }) {
  const max = Math.max(...items.map((i) => Math.abs(i.amount)), 1e-12);
  return (
    <div className="space-y-1">
      {items.map((i) => {
        const neg = i.amount < -1e-12;
        const Tag = onPick ? "button" : "div";
        return (
          <Tag
            key={i.key}
            type={onPick ? "button" : undefined}
            onClick={onPick ? () => onPick(i) : undefined}
            className={cn(
              "w-full grid items-center gap-3 py-1 px-1.5 -mx-1.5 rounded-lg text-left",
              onPick && "hover:bg-surface",
              activeKey === i.key && "bg-brand-light/60"
            )}
            style={{ gridTemplateColumns: `${labelWidth}px minmax(0,1fr) auto` }}
            title={onPick ? "Filter to this" : undefined}
          >
            <span className="text-[12.5px] truncate">{i.label}</span>
            <span className="h-3.5 rounded bg-surface overflow-hidden">
              <span className={cn("block h-full rounded", neg ? "bg-stock-out" : i.tone === "back" ? "bg-gold" : "bg-brand")} style={{ width: `${Math.max(2, (Math.abs(i.amount) / max) * 100)}%` }} />
            </span>
            <span className="text-right leading-tight min-w-[84px]">
              <Money value={i.amount} className="text-[12px]" places={places} />
              {i.sub && <span className="block text-[10.5px] text-muted2">{i.sub}</span>}
            </span>
          </Tag>
        );
      })}
    </div>
  );
}

/** Search the catalog and pick a track. */
export function TrackPicker({ onPick, autoFocus, placeholder = "Search your catalog — title, artist, ISRC…" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const req = useRef(0);
  useEffect(() => {
    if (!q.trim()) {
      setRows([]);
      return undefined;
    }
    const id = ++req.current;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/tracks", { params: { q } });
        if (id === req.current) setRows(data.tracks.slice(0, 6));
      } finally {
        if (id === req.current) setBusy(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <label className="relative block">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
        <input
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          aria-label="Search your catalog"
          className="w-full h-9 pl-9 pr-8 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring"
        />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted2" />}
      </label>
      {rows.length > 0 && (
        <div className="mt-1.5 rounded-lg border border-line bg-card divide-y divide-line overflow-hidden">
          {rows.map((t) => (
            <button key={t.track_id} type="button" onClick={() => onPick(t)} className="w-full text-left px-3 py-2 hover:bg-brand-light/50">
              <div className="text-[13px] font-semibold truncate">
                {t.title} <span className="font-normal text-muted2">— {t.artist || "no artist"}</span>
              </div>
              <div className="font-mono text-[11px] text-muted2">{t.isrc || "no ISRC"}</div>
            </button>
          ))}
        </div>
      )}
      {q.trim() && !busy && rows.length === 0 && <p className="text-[12px] text-muted2 mt-1.5">Nothing in your catalog matches that.</p>}
    </div>
  );
}

/** What each source calls things, so one page can show either. */
export function termsFor(source, status) {
  const bmi = source === "BMI";
  const mri = source === "MRI";
  return {
    bmi,
    mri,
    org: bmi ? "BMI" : mri ? "Music Reports" : "the MLC",
    code: bmi ? "BMI song number" : mri ? "MRI song ID" : "MLC song code",
    plays: bmi ? "performances" : mri ? "creations" : "plays",
    Plays: bmi ? "Performances" : mri ? "Creations" : "Plays",
    work: bmi ? "song" : "work",
    Works: bmi ? "Songs" : "Works",
    accrued: status === "accrued",
    held: status === "held",
    paid: status === "accrued" ? "Accrued" : status === "held" ? "Held" : "Paid",
    service: bmi ? "source" : "service",
    Services: bmi ? "Sources" : "Services",
    period: bmi ? "Quarter" : "Month",
    statements: bmi ? "Reports" : "Statements",
    doc: status === "accrued" ? "accrual report" : "statement",
  };
}

/** How far an accrued total is from being paid out. */
export function Threshold({ amount, threshold, className }) {
  if (!threshold) return null;
  const pct = Math.max(0, Math.min(100, (amount / threshold) * 100));
  return (
    <div className={className}>
      <div className="flex justify-between text-[12px] text-muted2 mb-1.5 gap-3">
        <span>Towards the {money(threshold, { places: 2 })} payout</span>
        <span className="font-mono">
          {money(amount, { places: 2 })} of {money(threshold, { places: 2 })}
        </span>
      </div>
      <div className="h-2 rounded-full bg-surface overflow-hidden">
        <div className="h-full rounded-full bg-gold" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** Music Reports song codes are stored as "MRI" + their song ID; show just the ID. */
export const shownCode = (code) => String(code || "").replace(/^MRI(?=\d)/, "");

/** What kind of money each source pays, for the source switcher. */
export const SOURCE_KIND = { MLC: "mechanical", BMI: "performance", MRI: "Music Reports" };
